import { useRef, useCallback, useEffect } from 'react';

const API_URL = 'https://api.doramahype.ru/api/v1/watch-time';
const FLUSH_INTERVAL_MS = 30_000;

/**
 * Отслеживает реальное время просмотра через state machine на основе событий Kodik.
 *
 * Реальный просмотр — только когда плеер играет (между play и pause/seek/end).
 * Скорость воспроизведения не влияет: считается wall-clock время перед экраном.
 *
 * Флаш накопленных секунд происходит:
 *  1. Каждые 30 секунд (setInterval)
 *  2. При смене эпизода (onEpisodeChange) — перед сбросом контекста
 *  3. При уходе со страницы (useEffect cleanup — SPA-навигация)
 *  4. При закрытии вкладки (beforeunload)
 *
 * @param {Object}  params
 * @param {string}  params.materialId  — UUID материала
 * @param {boolean} params.enabled     — активен только для авторизованных
 * @param {string}  params.accessToken — Bearer токен для Authorization header
 */
export function useWatchTimeTracker({ materialId, enabled, accessToken }) {
  // Текущий эпизод
  const episodeRef = useRef(0);
  // Играет ли плеер прямо сейчас
  const isPlayingRef = useRef(false);
  // Wall-clock момент когда последний раз начали играть (Date.now())
  const playStartRef = useRef(null);
  // Накопленные секунды с последнего флаша
  const accumulatedRef = useRef(0);

  // Стабильные refs для использования в замыканиях без пересоздания колбэков
  const materialIdRef = useRef(materialId);
  const enabledRef = useRef(enabled);
  const tokenRef = useRef(accessToken);

  useEffect(() => { materialIdRef.current = materialId; }, [materialId]);
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);
  useEffect(() => { tokenRef.current = accessToken; }, [accessToken]);

  // --- Вспомогательная: фиксирует секунды текущего интервала воспроизведения ---
  // Если плеер играет — считаем время от playStart до сейчас и добавляем в accumulated.
  // Если плеер стоит — ничего не делаем.
  const commitCurrentInterval = useCallback(() => {
    if (!isPlayingRef.current || playStartRef.current === null) return;
    const elapsed = Math.floor((Date.now() - playStartRef.current) / 1000);
    if (elapsed > 0) {
      accumulatedRef.current += elapsed;
    }
    playStartRef.current = Date.now(); // сбрасываем точку отсчёта
  }, []);

  // --- Флаш: отправляет накопленные секунды на сервер и сбрасывает счётчик ---
  const flush = useCallback((episode, seconds, token, matId) => {
    if (!enabledRef.current) return;
    if (seconds < 1 || !matId || !token) return;

    fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        material_id: matId,
        episode,
        seconds_watched: Math.min(seconds, 3600),
      }),
      // keepalive: запрос выживет даже при закрытии вкладки (Chrome 66+, FF 90+, Safari 17.2+)
      keepalive: true,
    }).catch(() => {}); // fire-and-forget, никогда не блокируем UI
  }, []);

  // --- Периодический флаш каждые 30 секунд ---
  useEffect(() => {
    if (!enabled) return;

    const id = setInterval(() => {
      commitCurrentInterval(); // фиксируем текущий интервал воспроизведения
      const seconds = accumulatedRef.current;
      if (seconds < 1) return;
      flush(episodeRef.current, seconds, tokenRef.current, materialIdRef.current);
      accumulatedRef.current = 0;
    }, FLUSH_INTERVAL_MS);

    return () => {
      clearInterval(id);
      // Флаш при SPA-навигации (React Router — beforeunload не срабатывает)
      commitCurrentInterval();
      const seconds = accumulatedRef.current;
      if (seconds >= 1) {
        flush(episodeRef.current, seconds, tokenRef.current, materialIdRef.current);
        accumulatedRef.current = 0;
      }
    };
  }, [enabled, flush, commitCurrentInterval]);

  // --- Флаш при закрытии вкладки ---
  useEffect(() => {
    if (!enabled) return;

    const handleUnload = () => {
      commitCurrentInterval();
      const seconds = accumulatedRef.current;
      if (seconds < 1) return;
      flush(episodeRef.current, seconds, tokenRef.current, materialIdRef.current);
      accumulatedRef.current = 0;
    };

    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [enabled, flush, commitCurrentInterval]);

  // --- Обработчики событий Kodik ---

  /** kodik_player_play: плеер начал/возобновил воспроизведение */
  const onPlay = useCallback(() => {
    if (!enabledRef.current) return;
    isPlayingRef.current = true;
    playStartRef.current = Date.now();
  }, []);

  /** kodik_player_pause: плеер встал на паузу */
  const onPause = useCallback(() => {
    if (!enabledRef.current) return;
    commitCurrentInterval();
    isPlayingRef.current = false;
    playStartRef.current = null;
  }, [commitCurrentInterval]);

  /**
   * kodik_player_seek: пользователь перемотал.
   * Если до seek плеер играл — фиксируем секунды до точки перемотки
   * и сбрасываем playStart (воспроизведение продолжается с новой позиции).
   */
  const onSeek = useCallback(() => {
    if (!enabledRef.current) return;
    if (isPlayingRef.current) {
      commitCurrentInterval();
      // playStart уже обновлён внутри commitCurrentInterval
    }
  }, [commitCurrentInterval]);

  /** kodik_player_video_ended: серия/фильм досмотрен до конца */
  const onVideoEnded = useCallback(() => {
    if (!enabledRef.current) return;
    commitCurrentInterval();
    isPlayingRef.current = false;
    playStartRef.current = null;
    // Немедленный флаш финальных секунд
    const seconds = accumulatedRef.current;
    if (seconds >= 1) {
      flush(episodeRef.current, seconds, tokenRef.current, materialIdRef.current);
      accumulatedRef.current = 0;
    }
  }, [commitCurrentInterval, flush]);

  /**
   * kodik_player_current_episode: пользователь переключил серию.
   * Флашим секунды предыдущего эпизода, затем переключаем контекст.
   */
  const onEpisodeChange = useCallback((episode) => {
    if (!enabledRef.current) return;
    commitCurrentInterval();
    const seconds = accumulatedRef.current;
    if (seconds >= 1) {
      flush(episodeRef.current, seconds, tokenRef.current, materialIdRef.current);
      accumulatedRef.current = 0;
    }
    episodeRef.current = episode;
    // После смены серии Kodik пришлёт kodik_player_play — тогда обновим playStart
    isPlayingRef.current = false;
    playStartRef.current = null;
  }, [commitCurrentInterval, flush]);

  return { onPlay, onPause, onSeek, onVideoEnded, onEpisodeChange };
}
